"use client";

import { useTranslations } from "use-intl";

import { CompanyIcon, ExpandIcon, HierarchyIcon, SellIcon } from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import { cn } from "@/lib/utils";

import type { RelatedEntity, RelatedEntityKind } from "./merge-related-search-entities";

const RELATED_ENTITY_ICONS: Record<RelatedEntityKind, AppIcon> = {
  imprint: SellIcon,
  company: CompanyIcon,
  mixed: HierarchyIcon,
};

export function RelatedSearchEntities({
  entities,
  expanded,
  onToggle,
}: {
  entities: RelatedEntity[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations("globals.search");
  const tBio = useTranslations("shared.bioText");
  if (entities.length === 0) return null;
  return (
    <section className="mb-8">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls="related-search-entities"
        onClick={onToggle}
        className="flex w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-left text-sm font-bold text-muted-foreground transition-colors hover:border-primary/40 hover:bg-secondary/40 hover:text-foreground"
      >
        <span>{t("relatedEntities")}</span>
        <span className="text-xs font-medium">({entities.length})</span>
        <span className="ml-auto text-xs text-primary">
          {expanded ? tBio("showLess") : tBio("showMore")}
        </span>
        <ExpandIcon
          className={cn("text-base transition-transform", expanded && "rotate-180")}
          aria-hidden
        />
      </button>
      {expanded && (
        <div id="related-search-entities" className="mt-2 flex flex-wrap items-center gap-1.5">
          {entities.map((entity) => {
            const Icon = RELATED_ENTITY_ICONS[entity.kind];
            return (
              <Link
                key={entity.id}
                to={`/company/${entity.id}`}
                className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-secondary hover:text-primary"
              >
                <Icon className="text-[14px] text-muted-foreground" />
                <span className="truncate">{entity.name}</span>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
